import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const OTAS = ['mmt', 'agoda', 'yatra'];
const OTA_LABEL = { mmt: 'MMT', agoda: 'Agoda', yatra: 'Yatra' };

// ONE unified OTA room/rate-plan mapping workspace, launched from Property
// Mapping → Create New Mapping → Load Mapping. Shows the selected properties as
// ONE hotel: a single internal-room table whose rows are the CANONICAL property's
// internal rooms. OTA columns are property-scoped dropdowns (union across the
// selected properties). Save Mapping saves the mappings AND consolidates the
// selected properties into one final property via /property-mapping/consolidate.
export const MappingWorkspace = ({ apiBase, finalName, propertyIds, data, open, onOpenChange, onSaved }) => {
  const hdr = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
  const [canonicalId, setCanonicalId] = useState('');
  const [rooms, setRooms] = useState({});   // room_type_id -> { ota: code }
  const [rates, setRates] = useState({});    // rate_plan_id -> { ota: code }
  const [expanded, setExpanded] = useState({});
  const [saving, setSaving] = useState(false);
  const [otaRatePlans, setOtaRatePlans] = useState({}); // `${ota}:${code}` -> [{code,name}]
  const [rpLoading, setRpLoading] = useState({});

  useEffect(() => {
    if (!data) return;
    const cId = data.canonical_property_id || '';
    setCanonicalId(cId);
    initDrafts(cId);
    setExpanded({});
    setOtaRatePlans({});
    setRpLoading({});
    // eslint-disable-next-line
  }, [data]);

  const initDrafts = (cId) => {
    const prop = (data?.properties || []).find(p => p.id === cId);
    const r = {}, rp = {};
    (prop?.room_types || []).forEach(rt => {
      r[rt.id] = {};
      OTAS.forEach(o => { r[rt.id][o] = rt.ota_mappings?.[o]?.ota_room_code || ''; });
      (rt.rate_plans || []).forEach(x => {
        rp[x.id] = {};
        OTAS.forEach(o => { rp[x.id][o] = x.ota_mappings?.[o]?.ota_rate_plan_code || ''; });
      });
    });
    setRooms(r); setRates(rp);
  };

  const canonicalProp = useMemo(
    () => (data?.properties || []).find(p => p.id === canonicalId),
    [data, canonicalId]);

  // YATRA ONLY: once a selected Yatra room's room-specific rate plans have
  // loaded, drop any previously-selected Yatra rate plan that is no longer valid
  // for the new room (reset that cell to blank). Other OTAs are untouched.
  useEffect(() => {
    const toClear = [];
    (canonicalProp?.room_types || []).forEach(rt => {
      const roomCode = (rooms[rt.id]?.yatra || '').trim();
      const k = `yatra:${roomCode}`;
      const opts = roomCode ? otaRatePlans[k] : undefined;
      if (!opts) return; // not loaded yet — don't clear prematurely
      (rt.rate_plans || []).forEach(rp => {
        const sel = rates[rp.id]?.yatra;
        if (sel && !opts.some(o => o.code === sel)) toClear.push(rp.id);
      });
    });
    if (toClear.length) {
      setRates(s => {
        const cp = { ...s };
        toClear.forEach(rpid => { cp[rpid] = { ...cp[rpid], yatra: '' }; });
        return cp;
      });
    }
    // eslint-disable-next-line
  }, [otaRatePlans, rooms, canonicalProp]);

  const invRooms = (ota) => data?.inventory?.[ota]?.rooms || [];
  const invRates = (ota) => data?.inventory?.[ota]?.rate_plans || [];

  // Display-only: resolve each OTA room code to its name for the dropdown label
  // ("Room Type Name (Room Type Code)"). Inventory name wins; when it's blank,
  // fall back to the ota_room_name already saved on any selected property's
  // mapping. Purely a label helper — no mapping logic / integration change.
  const otaRoomNames = useMemo(() => {
    const map = { mmt: {}, agoda: {}, yatra: {} };
    OTAS.forEach(o => (data?.inventory?.[o]?.rooms || []).forEach(r => {
      if (r.code && r.name) map[o][r.code] = r.name;
    }));
    (data?.properties || []).forEach(p => (p.room_types || []).forEach(rt => OTAS.forEach(o => {
      const m = rt.ota_mappings?.[o];
      if (m?.ota_room_code && m?.ota_room_name && !map[o][m.ota_room_code]) {
        map[o][m.ota_room_code] = m.ota_room_name;
      }
    })));
    return map;
  }, [data]);
  const roomOptions = (ota) => invRooms(ota).map(o => ({
    code: o.code, name: o.name || otaRoomNames[ota]?.[o.code] || '',
  }));

  // Reverse lookup: OTA room code -> INTERNAL room type name, taken from the
  // internal rooms' saved ota_mappings across all selected properties. Used only
  // to format the room dropdown label as "Internal Room Name (OTA Code)".
  const internalRoomNameByCode = useMemo(() => {
    const map = { mmt: {}, agoda: {}, yatra: {} };
    (data?.properties || []).forEach(p => (p.room_types || []).forEach(rt => OTAS.forEach(o => {
      const code = rt.ota_mappings?.[o]?.ota_room_code;
      if (code && rt.name && !map[o][code]) map[o][code] = rt.name;
    })));
    return map;
  }, [data]);
  const roomOptionLabel = (ota, code) => {
    const nm = internalRoomNameByCode[ota]?.[code];
    return nm ? `${nm} (${code})` : code;
  };

  // LIVE fetch of an OTA's rate plans for the selected OTA room. Triggered on
  // room-type SELECTION (and when expanding a row that already has a selection)
  // — never on initial page load. Idempotent per (ota, code).
  const fetchOtaRatePlans = async (ota, rawCode) => {
    const code = (rawCode || '').trim();
    if (!ota || !code) return;
    const k = `${ota}:${code}`;
    if (otaRatePlans[k] || rpLoading[k]) return;
    setRpLoading(s => ({ ...s, [k]: true }));
    try {
      const res = await axios.get(
        `${apiBase}/property-mapping/ota-rate-plans?property_ids=${propertyIds.join(',')}&ota=${ota}&room_code=${encodeURIComponent(code)}`, hdr);
      setOtaRatePlans(s => ({ ...s, [k]: res.data?.rate_plans || [] }));
      if (res.data && res.data.success === false && res.data.error) {
        toast.error(`${OTA_LABEL[ota]}: ${res.data.error}`);
      }
    } catch (e) {
      setOtaRatePlans(s => ({ ...s, [k]: [] }));
      toast.error(`${OTA_LABEL[ota]} rate plans: ${e.response?.data?.detail || 'fetch failed'}`);
    } finally {
      setRpLoading(s => ({ ...s, [k]: false }));
    }
  };

  const rpOptionsFor = (ota, selectedRoomCode) => {
    const code = (selectedRoomCode || '').trim();
    const k = `${ota}:${code}`;
    if (code && otaRatePlans[k]) return otaRatePlans[k];  // live, room-specific
    const all = invRates(ota);
    if (ota === 'agoda' && code) {
      const allowed = data?.agoda_rate_plans_by_room?.[code];
      if (allowed && allowed.length) return all.filter(rp => allowed.includes(rp.code));
    }
    return all;
  };

  const pickCanonical = (id) => { setCanonicalId(id); initDrafts(id); setExpanded({}); };

  const setRoomCode = (rtid, ota, code) => {
    setRooms(s => ({ ...s, [rtid]: { ...s[rtid], [ota]: code } }));
    if (code) fetchOtaRatePlans(ota, code);   // fetch rate plans on selection
  };
  const setRateCode = (rpid, ota, code) =>
    setRates(s => ({ ...s, [rpid]: { ...s[rpid], [ota]: code } }));

  const toggleExpand = (rt) => {
    const willOpen = !expanded[rt.id];
    setExpanded(e => ({ ...e, [rt.id]: willOpen }));
    if (willOpen) OTAS.forEach(ota => fetchOtaRatePlans(ota, rooms[rt.id]?.[ota]));
  };

  const invName = (ota, code) => (invRooms(ota).find(x => x.code === code) || {}).name || '';
  const invRateName = (ota, code) => {
    for (const [k, list] of Object.entries(otaRatePlans)) {
      if (k.startsWith(`${ota}:`)) {
        const f = (list || []).find(x => x.code === code);
        if (f) return f.name || '';
      }
    }
    return (invRates(ota).find(x => x.code === code) || {}).name || '';
  };

  const saveMapping = async () => {
    if (!canonicalId) { toast.error('Select the canonical source property first'); return; }
    const room_mappings = [];
    const rate_plan_mappings = [];
    (canonicalProp?.room_types || []).forEach(rt => {
      OTAS.forEach(ota => {
        const code = (rooms[rt.id]?.[ota] || '').trim();
        if (code) room_mappings.push({ room_type_id: rt.id, ota, ota_room_code: code, ota_room_name: invName(ota, code) });
      });
      (rt.rate_plans || []).forEach(rp => {
        OTAS.forEach(ota => {
          const code = (rates[rp.id]?.[ota] || '').trim();
          if (code) rate_plan_mappings.push({ rate_plan_id: rp.id, ota, ota_rate_plan_code: code, ota_rate_plan_name: invRateName(ota, code) });
        });
      });
    });
    try {
      setSaving(true);
      const res = await axios.post(`${apiBase}/property-mapping/consolidate`, {
        final_property_name: finalName.trim(),
        property_ids: propertyIds,
        canonical_property_id: canonicalId,
        room_mappings, rate_plan_mappings,
      }, hdr);
      const c = res.data?.cleanup || {};
      const extra = (propertyIds.length > 1)
        ? ` · consolidated ${propertyIds.length} → 1 · ${c.deleted_room_types || 0} duplicate rooms removed, ${c.hidden_room_types || 0} hidden`
        : '';
      toast.success(`Saved "${finalName.trim()}"${extra}`);
      onOpenChange(false);
      if (onSaved) await onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const Cell = ({ ota, kind, idSuffix, value, options, onChange, labelFor }) => (
    <select className="border rounded text-xs px-1 py-1 w-full max-w-[220px]"
      value={value || ''} onChange={e => onChange(e.target.value)}
      data-testid={`cell-${kind}-select-${ota}-${idSuffix}`}>
      <option value="">— Select {OTA_LABEL[ota]} —</option>
      {options.map(o => <option key={o.code} value={o.code}>{labelFor ? labelFor(o.code) : (o.name ? `${o.name} (${o.code})` : o.code)}</option>)}
    </select>
  );

  const ambiguous = !canonicalId;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[85vh] overflow-y-auto" data-testid="ota-mapping-workspace">
        <DialogHeader>
          <DialogTitle>OTA Room/Rate Mapping — {finalName || 'Workspace'}</DialogTitle>
        </DialogHeader>

        {/* Canonical source resolution */}
        {data?.ambiguous && (
          <div className="border border-amber-300 bg-amber-50 rounded p-3 mb-2" data-testid="canonical-ambiguous">
            <div className="text-xs font-semibold text-amber-800 mb-2">
              Ambiguous canonical source — please select the property whose internal rooms are the canonical/base rooms. Save is disabled until you choose.
            </div>
            <div className="flex flex-col gap-1">
              {(data?.properties || []).map(p => (
                <label key={p.id} className="flex items-center gap-2 text-xs cursor-pointer" data-testid={`canonical-pick-${p.id}`}>
                  <input type="radio" name="canonical-src" checked={canonicalId === p.id} onChange={() => pickCanonical(p.id)} />
                  <span className="font-medium">{p.name}</span>
                  <span className="text-gray-500">· base: {p.base_ota || 'ambiguous'} · {(p.room_types || []).length} rooms{p.is_canonical_candidate ? ' · candidate' : ''}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {!data?.ambiguous && canonicalProp && (
          <div className="text-xs text-gray-500 mb-1" data-testid="canonical-source-label">
            Canonical source: <span className="font-semibold text-gray-700">{canonicalProp.name}</span>
            {propertyIds.length > 1 && <> · {propertyIds.length} properties will be consolidated into one on Save</>}
          </div>
        )}

        <div className="border rounded" data-testid="mapping-single-card">
          <div className="flex items-center justify-between bg-gray-50 px-3 py-2">
            <div className="text-sm font-semibold">{finalName || canonicalProp?.name || 'Final Property'}</div>
            <Button size="sm" onClick={saveMapping} disabled={saving || ambiguous} data-testid="workspace-save-btn">
              {saving ? 'Saving…' : 'Save Mapping'}
            </Button>
          </div>
          <div className="p-3 overflow-x-auto">
            {ambiguous ? (
              <div className="text-xs text-gray-400 py-4">Select a canonical source above to view the internal room table.</div>
            ) : (
              <table className="w-full text-xs">
                <thead><tr className="text-gray-500 text-left">
                  <th className="py-1 w-8"></th><th>Internal Room Type</th>
                  {OTAS.map(o => <th key={o}>{OTA_LABEL[o]}</th>)}
                </tr></thead>
                <tbody>
                  {(canonicalProp?.room_types || []).map(rt => {
                    const key = rt.id;
                    return (
                      <React.Fragment key={rt.id}>
                        <tr className="border-t" data-testid={`room-row-${rt.id}`}>
                          <td><button className="text-gray-500" onClick={() => toggleExpand(rt)} data-testid={`expand-room-${rt.id}`}>{expanded[key] ? '▼' : '▶'}</button></td>
                          <td className="font-medium">{rt.name}</td>
                          {OTAS.map(ota => (
                            <td key={ota} className="py-1">
                              <Cell ota={ota} kind="room" idSuffix={rt.id} value={rooms[rt.id]?.[ota]}
                                options={roomOptions(ota)}
                                labelFor={(code) => roomOptionLabel(ota, code)}
                                onChange={(v) => setRoomCode(rt.id, ota, v)} />
                            </td>
                          ))}
                        </tr>
                        {expanded[key] && (
                          <tr className="bg-gray-50/50"><td></td><td colSpan={4} className="py-2">
                            <div className="text-xs font-semibold mb-1 text-gray-600">Rate Plan Mapping</div>
                            <table className="w-full text-xs">
                              <thead><tr className="text-gray-400 text-left">
                                <th>Internal Rate Plan</th>{OTAS.map(o => <th key={o}>{OTA_LABEL[o]}</th>)}
                              </tr></thead>
                              <tbody>
                                {(rt.rate_plans || []).map(rp => (
                                  <tr key={rp.id} data-testid={`rate-row-${rp.id}`}>
                                    <td className="py-1 font-medium">{rp.name || rp.code}</td>
                                    {OTAS.map(ota => {
                                      const selRoom = rooms[rt.id]?.[ota];
                                      const loading = selRoom && rpLoading[`${ota}:${(selRoom || '').trim()}`];
                                      return <td key={ota} className="py-1">
                                        <Cell ota={ota} kind="rate" idSuffix={rp.id} value={rates[rp.id]?.[ota]}
                                          options={rpOptionsFor(ota, selRoom)}
                                          onChange={(v) => setRateCode(rp.id, ota, v)} />
                                        {loading && <span className="text-[10px] text-gray-400" data-testid={`rate-loading-${ota}-${rp.id}`}>Loading rate plans…</span>}
                                      </td>;
                                    })}
                                  </tr>
                                ))}
                                {!(rt.rate_plans || []).length && <tr><td colSpan={4} className="text-gray-400">No rate plans</td></tr>}
                              </tbody>
                            </table>
                          </td></tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {!(canonicalProp?.room_types || []).length && <tr><td colSpan={5} className="text-gray-400 py-2">No canonical internal rooms</td></tr>}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MappingWorkspace;
